import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { withBranch } from '../../utils/branchRequest';

const API_URL = '/api/vehicles';

export const fetchVehicles = createAsyncThunk('vehicles/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const response = await axios.get(API_URL, { params });
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

export const createVehicle = createAsyncThunk('vehicles/create', async (data, { rejectWithValue }) => {
  try {
    // __branchId pins the create to the branch picked before the form opened;
    // without it the call keeps its base shape.
    const { __branchId, ...body } = data || {};
    const response = __branchId
      ? await axios.post(API_URL, body, withBranch(__branchId))
      : await axios.post(API_URL, data);
    // The vehicle itself. The reducer must NOT unwrap this again.
    return response.data.data;
  } catch (error) {
    // Three shapes reach here and only one used to be read. The POST route runs
    // express-validator, which answers `{ success:false, errors:[{msg}] }` —
    // PLURAL, an array — so `data.error` was undefined for the single most
    // common failure and the toast fell back to axios's own
    // "Request failed with status code 400". The user is told a required field
    // is missing by being told nothing at all.
    const body = error.response?.data;
    const fromValidator = Array.isArray(body?.errors)
      ? body.errors.map((e) => e.msg || e.message).filter(Boolean).join(' • ')
      : null;
    // Reject with the BODY, like updateVehicle does. Returning the joined
    // string here discarded `code` and `fieldErrors`, so the duplicate-plate
    // refusal reached the user as raw English with nothing highlighted.
    // fromValidator is preserved as the error text when that shape arrives.
    if (body && typeof body === 'object') {
      return rejectWithValue(fromValidator ? { ...body, error: fromValidator } : body);
    }
    return rejectWithValue({ error: fromValidator || error.message });
  }
});

export const updateVehicle = createAsyncThunk('vehicles/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await axios.patch(`${API_URL}/${id}`, data);
    return response.data;

  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
});

const vehicleSlice = createSlice({
  name: 'vehicles',
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVehicles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.items = action.payload.data;
        state.loading = false;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        // `action.payload` IS the vehicle — the thunk already unwrapped
        // response.data.data. Unwrapping a second time yielded undefined and
        // pushed it to the TOP of the list, so the next render called
        // renderPlate(undefined), read .plate_number off it, and the
        // ErrorBoundary replaced the whole page with "عذرًا! حدث خطأ ما".
        //
        // The vehicle was created regardless, which is why a refresh showed it:
        // fetchVehicles rebuilds the list from the server and the bad row is
        // gone. updateVehicle below returns the envelope and unwraps once, so
        // only create was inconsistent.
        if (action.payload) state.items.unshift(action.payload);
      })
      .addCase(updateVehicle.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.data.id);
        if (index !== -1) {
          state.items[index] = action.payload.data;
        }
      });
  },
});

export default vehicleSlice.reducer;
